// Lightweight rich-content model shared by the FAQ and the resource articles.
//
// A document is an array of BLOCKS. A block holds inline SPANS.
//   block:  { type: "p" | "h2" | "h3" | "ul" | "ol" | "tldr" | "callout", ... }
//   span:   string | { text, href?, bold?, italic? }
//
// The same data renders to the UI (RichText component) AND flattens to plain
// text for JSON-LD, meta descriptions, and answer-engine extraction. Keeping a
// single plain-text-derivable source is what lets us emit FAQPage / Article
// schema without duplicating copy (the old FAQ stored answers as JSX, which
// could not be turned into structured data).

/** @typedef {string | { text: string, href?: string, bold?: boolean, italic?: boolean }} Span */

/** @typedef {Span | Span[]} ListItem */
/**
 * @typedef {Object} RichBlock
 * @property {string} type
 * @property {Span[]} [spans]
 * @property {string} [text]
 * @property {ListItem[]} [items]
 * @property {string | number} [value]
 * @property {string} [label]
 * @property {string[]} [headers]
 * @property {ListItem[][]} [rows]
 * @property {string} [id]
 */

/**
 * Plain text of a single span.
 * @param {Span | null | undefined} span
 * @returns {string}
 */
export const spanText = (span) => (typeof span === "string" ? span : span?.text || "");

/**
 * Plain text of an array of spans.
 * @param {Span[]} [spans]
 * @returns {string}
 */
export const spansText = (spans = []) => spans.map(spanText).join("");

/** @param {ListItem} item @returns {string} */
const itemText = (item) => spansText(Array.isArray(item) ? item : [item]);

/**
 * Flatten one block to plain text.
 * @param {RichBlock} block
 * @returns {string}
 */
export const blockToPlainText = (block) => {
  switch (block.type) {
    case "p":
    case "tldr":
    case "callout":
      return spansText(block.spans);
    case "h2":
    case "h3":
      return block.text || "";
    case "ul":
    case "ol":
      return (block.items || []).map(itemText).join(" ");
    case "stat":
      return [block.value, block.label].filter(Boolean).join(" ");
    case "table": {
      const head = (block.headers || []).join(" | ");
      const body = (block.rows || []).map((row) => row.map(itemText).join(" | ")).join("\n");
      return [head, body].filter(Boolean).join("\n");
    }
    default:
      return "";
  }
};

/**
 * Flatten an array of blocks to plain text (paragraphs separated by blank lines).
 * @param {RichBlock[]} [blocks]
 * @returns {string}
 */
export const blocksToPlainText = (blocks = []) =>
  blocks.map(blockToPlainText).filter(Boolean).join("\n\n");
